import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Clock, CheckCircle, XCircle, Loader2, ArrowRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const VerifyEmailPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [status, setStatus] = useState('loading'); // loading, verifying, success, error
  const [errorMessage, setErrorMessage] = useState('');
  const [companyName, setCompanyName] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setErrorMessage('No verification token provided');
      return;
    }
    
    verifyEmail();
  }, [token]);

  const verifyEmail = async () => {
    setStatus('verifying');
    
    try {
      const response = await axios.post(`${API}/api/signup/verify`, {
        token: token
      });
      
      setCompanyName(response.data.company_name);
      setStatus('success');
      toast.success('Email verified successfully!');
      
      // Auto-redirect after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (error) {
      setStatus('error');
      setErrorMessage(error.response?.data?.detail || 'Verification failed. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-900 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2">
            <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
              <Clock className="w-7 h-7 text-white" />
            </div>
            <span className="text-2xl font-bold text-white">ClockIn Pro</span>
          </Link>
        </div>

        <Card data-testid="verify-email-card">
          {/* Loading State */}
          {(status === 'loading' || status === 'verifying') && (
            <>
              <CardHeader className="text-center">
                <div className="mx-auto w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mb-4">
                  <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
                </div>
                <CardTitle>Verifying Your Email</CardTitle>
                <CardDescription>
                  Please wait while we verify your email address...
                </CardDescription>
              </CardHeader>
            </>
          )}

          {/* Success State */}
          {status === 'success' && (
            <>
              <CardHeader className="text-center">
                <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <CardTitle className="text-green-700">Email Verified!</CardTitle>
                <CardDescription className="text-base">
                  Your account for <span className="font-medium text-slate-900">{companyName}</span> is now active.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                  <p className="text-sm text-green-800">
                    You can now sign in to your dashboard and start setting up your company.
                  </p>
                </div>
                
                <Link to="/login" className="block">
                  <Button className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700" data-testid="go-to-login-btn">
                    Go to Sign In
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                
                <p className="text-xs text-center text-slate-500">
                  Redirecting to login in a few seconds...
                </p>
              </CardContent>
            </>
          )}

          {/* Error State */}
          {status === 'error' && (
            <>
              <CardHeader className="text-center">
                <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                  <XCircle className="w-8 h-8 text-red-600" />
                </div>
                <CardTitle className="text-red-700">Verification Failed</CardTitle>
                <CardDescription className="text-base">
                  {errorMessage}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm text-red-800">
                    This could happen if:
                  </p>
                  <ul className="text-sm text-red-700 mt-2 space-y-1 list-disc list-inside">
                    <li>The verification link has expired (24 hours)</li>
                    <li>The link was already used</li>
                    <li>The link is invalid or incomplete</li>
                  </ul>
                </div>
                
                <div className="space-y-2">
                  <Link to="/signup" className="block">
                    <Button className="w-full" variant="outline" data-testid="try-again-btn">
                      Sign Up Again
                    </Button>
                  </Link>
                  <Link to="/login" className="block">
                    <Button className="w-full" variant="ghost">
                      Back to Sign In
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </>
          )}
        </Card>
      </div>
    </div>
  );
};

export default VerifyEmailPage;
